import React from 'react';

import { AutoLink } from '@betterment/js-runtime';
import { H1 } from '@betterment/samba-react';
import {
  Box,
  ContentLayoutE,
  Flex,
  Loader,
  SectionLayout,
  Text,
  theme,
  useTheme,
} from '@betterment/style-closet-react';
import styled from '@emotion/styled';

import BottomRail from './BottomRail';
import { DismissalModal } from './DismissalModal';
import ErrorSection from './ErrorSection';
import { FilterTabs } from './FilterTabs';
import { FirstImpressionCard } from './FirstImpressionCard';
import FlashSection from './FlashSection';
import NoNewItems from './NoNewItems';

import { ItemWithID, useCoPilotSectionState, useRerendCountOnDismiss } from '../../contexts/CoPilotSectionContext';
import { useGetNotificationsCounts } from '../../services/api/clients';
import { Paths, Tabs } from '../../utils/co_pilot';
import { Header } from '../BettermentForAdvisors/Header';
import { Layout as MainLayout } from '../InstitutionalApp/Layout';

interface NavitemProps {
  itemText: Tabs;
  active: boolean;
  path: Paths;
  totalRecords: number;
}

export const StyledCheckbox = styled.input`
  width: ${theme.space[6]};
  height: ${theme.space[6]};
  margin: 0 ${theme.space[6]} 0 0;
  display: inline-block;
`;

const ShadowScrollWrapper = styled(Flex)`
  background:
    0,
    linear-gradient(${theme.colors.bmtWhite}, ${theme.colors.bmtWhite} 70%) 0 100%,
    0,
    linear-gradient(rgba(0, 0, 0, 0), ${theme.colors.bmtGrey20}) 0 100%;
  background-repeat: no-repeat;
  background-color: ${theme.colors.bmtWhite};
  background-size: 100% 14px;
  background-attachment: local, local, scroll, scroll;
`;

const NoWrapBox = styled(Box)`
  white-space: nowrap;
`;

const NavHeader: React.FC<React.PropsWithChildren<{ headerText: string }>> = ({ headerText }) => {
  return (
    <Box pb={2} pl={1}>
      <Text fontWeight="700" color="bmtGrey50" textTransform="uppercase">
        {headerText}
      </Text>
    </Box>
  );
};

const NavItem: React.FC<React.PropsWithChildren<NavitemProps>> = ({ itemText, active, path, totalRecords }) => {
  return (
    <AutoLink to={path}>
      <NoWrapBox backgroundColor={active && 'bmtGrey20'} width="70%" pl={2} pr={2} mb={2} minWidth={200}>
        <Flex justifyContent="space-between">
          <Text>{itemText}</Text>
          {totalRecords !== 0 && <Text mr="m">{totalRecords}</Text>}
        </Flex>
      </NoWrapBox>
    </AutoLink>
  );
};

interface Props {
  activeTab: Tabs;
  header: string;
  subHeader: string;
  tableFooter?: React.ReactElement;
}

const Layout: React.FC<React.PropsWithChildren<Props>> = ({ activeTab, header, subHeader, tableFooter, children }) => {
  const { theme: themeConfig } = useTheme();
  const { status, handleDismissals, handleToggleModal } = useCoPilotSectionState<ItemWithID>();
  const { data, refetch: refetchCounts, loading } = useGetNotificationsCounts();

  useRerendCountOnDismiss(data, refetchCounts, loading);

  const notificationKeyByActiveTab: { [key: string]: string } = {
    'Account approvals': 'accountApprovals',
    'Outstanding RMDs': 'rmds',
    'Pending invites': 'pendingInvites',
    'Missing beneficiaries': 'missingBeneficiaries',
    'Onboarding lockouts': 'accountLockouts',
    'Pending ACATS': 'pendingAcats',
    'Failed ACATS': 'failedAcats',
    'ACATS Approvals': 'acatsApprovals',
  };

  return (
    <MainLayout active="co_pilot">
      <MainLayout.Header>
        <Header title="Co-pilot" />
      </MainLayout.Header>
      <MainLayout.Content>
        <SectionLayout>
          <SectionLayout.ContentWrapper>
            <FirstImpressionCard />
            <ContentLayoutE>
              <ContentLayoutE.Slot1 minWidth={200}>
                <Box>
                  <NavHeader headerText="Accounts" />
                  <NavItem
                    itemText="Account approvals"
                    active={activeTab === 'Account approvals'}
                    path="/co_pilot/account_approvals"
                    totalRecords={data?.total?.accountApprovals || 0}
                  />
                  <NavItem
                    itemText="Outstanding RMDs"
                    active={activeTab === 'Outstanding RMDs'}
                    path="/co_pilot/rmds"
                    totalRecords={data?.total?.rmds || 0}
                  />
                  <Box mt={9}>
                    <NavHeader headerText="Clients" />
                    <NavItem
                      itemText="Pending invites"
                      active={activeTab === 'Pending invites'}
                      path="/co_pilot/pending_invites"
                      totalRecords={data?.total?.pendingInvites || 0}
                    />
                    <NavItem
                      itemText="Missing beneficiaries"
                      active={activeTab === 'Missing beneficiaries'}
                      path="/co_pilot/missing_beneficiaries"
                      totalRecords={data?.total?.missingBeneficiaries || 0}
                    />
                    <NavItem
                      itemText="Onboarding lockouts"
                      active={activeTab === 'Onboarding lockouts'}
                      path="/co_pilot/account_lockouts"
                      totalRecords={data?.total?.accountLockouts || 0}
                    />
                  </Box>
                  <Box mt={9}>
                    <NavHeader headerText="Transactions" />
                    <NavItem
                      itemText="Pending ACATS"
                      active={activeTab === 'Pending ACATS'}
                      path="/co_pilot/pending_acats"
                      totalRecords={data?.total?.pendingAcats || 0}
                    />
                    <NavItem
                      itemText="Failed ACATS"
                      active={activeTab === 'Failed ACATS'}
                      path="/co_pilot/failed_acats"
                      totalRecords={data?.total?.failedAcats || 0}
                    />
                    <NavItem
                      itemText="ACATS Approvals"
                      active={activeTab === 'ACATS Approvals'}
                      path="/co_pilot/acats_approvals"
                      totalRecords={data?.total?.acatsApprovals || 0}
                    />
                  </Box>
                </Box>
              </ContentLayoutE.Slot1>
              <ContentLayoutE.Slot2>
                <Box
                  backgroundColor="bmtWhite"
                  minHeight="530px"
                  overflow="auto"
                  borderRadius={themeConfig.space[1]}
                  boxShadow={`0px 1px 0px ${themeConfig.colors.bmtGrey30}`}
                >
                  <Box px={8} pt={8}>
                    <Box>
                      <H1 variant="title200">{header}</H1>
                    </Box>
                    <Box pb={8}>
                      <Text color="bmtGrey50">{subHeader}</Text>
                    </Box>
                    <FilterTabs
                      primaryCount={data?.primary?.[notificationKeyByActiveTab[activeTab]] as number | undefined}
                      secondaryCount={data?.secondary?.[notificationKeyByActiveTab[activeTab]] as number | undefined}
                    />
                  </Box>
                  <Loader isLoading={status === 'loading'} backgroundColor="bmtWhite" width="100%" height="400px">
                    <FlashSection />
                    <ShadowScrollWrapper px={4} height="400px" overflow="scroll" flexDirection="column">
                      {children}
                      <Flex.Item flex="1">
                        <Flex alignItems="center" height="100%" justifyContent="center">
                          {status === 'empty' && <NoNewItems />}
                          {status === 'fetchError' && <ErrorSection />}
                        </Flex>
                      </Flex.Item>
                    </ShadowScrollWrapper>
                    <BottomRail hasTableFooter={!!tableFooter} />
                  </Loader>
                  {tableFooter}
                </Box>
                <DismissalModal
                  isOpen={status === 'modalOpen'}
                  submitHandler={handleDismissals}
                  closeHandler={handleToggleModal}
                />
              </ContentLayoutE.Slot2>
            </ContentLayoutE>
          </SectionLayout.ContentWrapper>
        </SectionLayout>
      </MainLayout.Content>
    </MainLayout>
  );
};

export default Layout;
